/**
 * RecordProceduresTab — Ustawienia → „Procedury SQL" → zakładka „Kartoteki".
 *
 * Katalog wielokrotnego użytku procedur SQL (walidacja/akcja) podpinanych do momentów cyklu
 * życia kartoteki (control/saved/delete) albo uruchamianych ręcznie z przycisku SQL na formularzu
 * rekordu (button) — mirror `processes/ProcessProceduresTab.tsx`.
 * Katalog jest jednorodny (brak origin local/global) — jeden gate `canManage` bramkuje
 * wszystko (podgląd, CRUD, restore) — mirror `useCanManageRecordProcedures` / backend
 * `crm_core.record_procedures.manage`.
 *
 * Toggle „Pokaż usunięte" wysyła `include_deleted=true` — backend zwraca wtedy też
 * soft-skasowane procedury, a przy nich renderuje się przycisk „Przywróć" (undelete).
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — ConfirmDialog + useToast.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { useCanManageRecordProcedures } from '@/hooks/useCanManageRecordProcedures';
import {
  RECORD_TYPE_OPTIONS,
  type RecordLifecycleEvent,
  type RecordProcedure,
  useCreateRecordProcedure,
  useDeleteRecordProcedure,
  useRecordProceduresList,
  useRestoreRecordProcedure,
  useUpdateRecordProcedure,
} from '@/lib/record-procedures/api';
import { testSqlErrorKey } from '@/lib/record-procedures/error-keys';

import {
  emptyRecordProcedureFormValues,
  RecordProcedureFormModal,
  type RecordProcedureFormValues,
} from './RecordProcedureFormModal';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Etykieta typu kartoteki — mirror `parentLabel` (`AdditionalViewsTab.tsx`), z fallbackiem na
 *  surowy string dla typów jeszcze niewpisanych do `RECORD_TYPE_OPTIONS`. */
function recordTypeLabel(t: ReturnType<typeof useTranslation>['t'], recordType: string): string {
  const known = RECORD_TYPE_OPTIONS.find((opt) => opt.value === recordType);
  return known ? t(known.labelKey) : recordType;
}

const LIFECYCLE_BADGE_COLOR: Record<RecordLifecycleEvent, BadgeColor> = {
  control: 'blue',
  saved: 'green',
  delete: 'red',
  button: 'accent',
};

export function RecordProceduresTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const canManage = useCanManageRecordProcedures();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [showDeleted, setShowDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RecordProcedure | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RecordProcedure | null>(null);
  // Błąd 409 (nazwa/slot zajęty) z ostatniej próby zapisu — pokazywany inline w modalu
  // (§4.12), reset przy każdym otwarciu/zamknięciu i tuż przed próbą zapisu.
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const listQ = useRecordProceduresList({ q: debouncedSearch, includeDeleted: showDeleted });
  const createMutation = useCreateRecordProcedure();
  const updateMutation = useUpdateRecordProcedure();
  const deleteMutation = useDeleteRecordProcedure();
  const restoreMutation = useRestoreRecordProcedure();

  if (!canManage) {
    return (
      <Card padding="md" data-testid="record-procedures-tab-no-permission">
        <p className="text-sm text-text-tertiary">{t('recordProcedures.list.noPermission')}</p>
      </Card>
    );
  }

  const formInitialValues: RecordProcedureFormValues = editing
    ? {
        name: editing.name,
        description: editing.description ?? '',
        recordType: editing.recordType,
        lifecycleEvent: editing.lifecycleEvent,
        sql: editing.sql,
        declaredParams: editing.declaredParams,
        timeoutMinutes: editing.timeoutMinutes,
      }
    : emptyRecordProcedureFormValues();

  function conflictErrorCode(err: unknown): string | null {
    return isAxiosError(err) ? ((err.response?.data?.error as string | undefined) ?? null) : null;
  }

  /**
   * `POST`/`PATCH .../record-procedures` wołają TEN SAM walidator strukturalny SQL PRZED
   * zapisem jak `test-sql` (patrz docstring `error-keys.ts`) — więc create/update mogą
   * wrócić z tymi samymi `SQL_<code>` (400) / `VALIDATION_FAILED` (422 Zod); PATCH dodatkowo
   * z `TIMEOUT_NOT_ALLOWED` (400, limit czasu dla procedury innej niż „Na przycisk") albo
   * `PARAMS_NOT_ALLOWED` (400, niepusta lista parametrów procedury „Na przycisk"). Bez tego
   * mapowania user dostawał tylko generyczny „nie udało się zapisać", tracąc realny powód (np.
   * który słowo kluczowe jest zabronione, który parametr jest niezadeklarowany).
   */
  function sqlOrValidationErrorTitle(err: unknown): string | null {
    if (!isAxiosError(err)) return null;
    const data = err.response?.data as
      | { error?: string; message?: string; paramName?: string }
      | undefined;
    const code = data?.error;
    if (
      !code ||
      !(
        code === 'VALIDATION_FAILED' ||
        code === 'TIMEOUT_NOT_ALLOWED' ||
        code === 'PARAMS_NOT_ALLOWED' ||
        code.startsWith('SQL_')
      )
    ) {
      return null;
    }
    const key = testSqlErrorKey(code);
    const translated = t(key, { paramName: data?.paramName ?? '' });
    if (key === 'recordProcedures.form.sqlErrors.generic' && data?.message) {
      return `${translated} ${data.message}`;
    }
    return translated;
  }

  function handleCreate(values: RecordProcedureFormValues) {
    setServerErrors({});
    createMutation.mutate(
      {
        name: values.name,
        description: values.description.trim() || null,
        recordType: values.recordType,
        lifecycleEvent: values.lifecycleEvent,
        sql: values.sql,
        declaredParams: values.declaredParams,
        // Serwer odrzuca `timeoutMinutes` (także `null`) poza „Na przycisk" — pole pomijamy.
        ...(values.timeoutMinutes !== null ? { timeoutMinutes: values.timeoutMinutes } : {}),
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('recordProcedures.toast.created') });
          setCreateOpen(false);
        },
        onError: (err) => {
          const code = conflictErrorCode(err);
          if (code === 'NAME_TAKEN') {
            toast.show({ variant: 'warning', title: t('recordProcedures.toast.nameTaken') });
            setServerErrors({ name: t('recordProcedures.toast.nameTaken') });
            return;
          }
          if (code === 'SLOT_TAKEN') {
            toast.show({ variant: 'warning', title: t('recordProcedures.toast.slotTaken') });
            return;
          }
          const sqlErrorTitle = sqlOrValidationErrorTitle(err);
          if (sqlErrorTitle) {
            toast.show({ variant: 'error', title: sqlErrorTitle });
            return;
          }
          toast.show({ variant: 'error', title: t('recordProcedures.toast.createError') });
        },
      },
    );
  }

  function handleUpdate(id: string, values: RecordProcedureFormValues) {
    setServerErrors({});
    updateMutation.mutate(
      {
        id,
        values: {
          name: values.name,
          description: values.description.trim() || null,
          sql: values.sql,
          declaredParams: values.declaredParams,
          // Limit czasu dotyczy tylko procedur „Na przycisk" (dla innych serwer → 400).
          ...(values.timeoutMinutes !== null ? { timeoutMinutes: values.timeoutMinutes } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('recordProcedures.toast.updated') });
          setEditing(null);
        },
        onError: (err) => {
          const code = conflictErrorCode(err);
          if (code === 'NAME_TAKEN') {
            toast.show({ variant: 'warning', title: t('recordProcedures.toast.nameTaken') });
            setServerErrors({ name: t('recordProcedures.toast.nameTaken') });
            return;
          }
          const sqlErrorTitle = sqlOrValidationErrorTitle(err);
          if (sqlErrorTitle) {
            toast.show({ variant: 'error', title: sqlErrorTitle });
            return;
          }
          toast.show({ variant: 'error', title: t('recordProcedures.toast.updateError') });
        },
      },
    );
  }

  function handleDelete(procedure: RecordProcedure) {
    deleteMutation.mutate(procedure.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('recordProcedures.toast.deleted') });
        setPendingDelete(null);
      },
      onError: () => {
        toast.show({ variant: 'error', title: t('recordProcedures.toast.deleteError') });
        setPendingDelete(null);
      },
    });
  }

  function handleRestore(procedure: RecordProcedure) {
    restoreMutation.mutate(procedure.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('recordProcedures.toast.restored') });
      },
      onError: (err) => {
        const code = conflictErrorCode(err);
        if (code === 'NAME_TAKEN' || code === 'SLOT_TAKEN') {
          toast.show({
            variant: 'warning',
            title: t(
              code === 'NAME_TAKEN'
                ? 'recordProcedures.toast.nameTaken'
                : 'recordProcedures.toast.slotTaken',
            ),
          });
          return;
        }
        toast.show({ variant: 'error', title: t('recordProcedures.toast.restoreError') });
      },
    });
  }

  const procedures = listQ.data ?? [];

  return (
    <div data-testid="record-procedures-tab">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('recordProcedures.list.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('recordProcedures.list.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('recordProcedures.list.searchPlaceholder')}
            prefixIcon={<Search className="h-4 w-4" />}
            data-testid="record-procedures-search"
          />
          <Checkbox
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            label={t('recordProcedures.list.showDeleted')}
            data-testid="record-procedures-show-deleted"
          />
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setServerErrors({});
              setCreateOpen(true);
            }}
            data-testid="record-procedures-new-btn"
          >
            {t('recordProcedures.list.newProcedure')}
          </Button>
        </div>
      </header>

      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : procedures.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('recordProcedures.list.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('recordProcedures.list.col.name')}</th>
                  <th className="px-4 py-2 text-left">
                    {t('recordProcedures.list.col.recordType')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('recordProcedures.list.col.lifecycleEvent')}
                  </th>
                  <th className="px-4 py-2 text-left">{t('recordProcedures.list.col.params')}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {procedures.map((procedure) => {
                  const isDeleted = procedure.gcrecord != null;
                  return (
                    <tr key={procedure.id} data-testid={`record-procedure-row-${procedure.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-text-primary">{procedure.name}</div>
                        {procedure.description && (
                          <div className="text-xs text-text-tertiary">{procedure.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {recordTypeLabel(t, procedure.recordType)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={LIFECYCLE_BADGE_COLOR[procedure.lifecycleEvent]}>
                          {t(`recordProcedures.lifecycleEvent.${procedure.lifecycleEvent}`)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {procedure.declaredParams.length}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isDeleted ? (
                            <IconButton
                              size="sm"
                              onClick={() => handleRestore(procedure)}
                              aria-label={t('recordProcedures.list.restore')}
                              data-testid={`record-procedure-restore-${procedure.id}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </IconButton>
                          ) : (
                            <>
                              <IconButton
                                size="sm"
                                onClick={() => {
                                  setServerErrors({});
                                  setEditing(procedure);
                                }}
                                aria-label={t('common.edit')}
                                data-testid={`record-procedure-edit-${procedure.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </IconButton>
                              <IconButton
                                variant="danger"
                                size="sm"
                                onClick={() => setPendingDelete(procedure)}
                                aria-label={t('common.delete')}
                                data-testid={`record-procedure-delete-${procedure.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </IconButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <RecordProcedureFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        initialValues={formInitialValues}
        serverErrors={serverErrors}
        saving={createMutation.isPending || updateMutation.isPending}
        onSubmit={(values) => {
          if (editing) {
            handleUpdate(editing.id, values);
          } else {
            handleCreate(values);
          }
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
          setServerErrors({});
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('recordProcedures.confirmDelete.title')}
        message={
          pendingDelete
            ? t(
                // Procedura „Na przycisk" ma inne skutki usunięcia: przyciski na formularzach, które
                // jej używają, przestają działać (nie ma czego „odpinać" od momentu uruchomienia).
                pendingDelete.lifecycleEvent === 'button'
                  ? 'recordProcedures.confirmDelete.messageButton'
                  : 'recordProcedures.confirmDelete.message',
                { name: pendingDelete.name },
              )
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
